import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import useDeviceGeolocation from '@sophocles/hooks/useDeviceGeolocation';

/**
 * Populate two formik fields with the device's current GPS fix.
 *
 * Used on both the Site Settings page Location section and the
 * EditSiteDialog so the same affordance and decimal precision apply
 * everywhere.  Defaults to writing into `location.latitude` /
 * `location.longitude` but the field paths are configurable for
 * surfaces that nest the form differently.
 */
export default function UseDeviceLocationButton({
  latitudeField = 'location.latitude',
  longitudeField = 'location.longitude',
  size = 'small',
  disabled,
}) {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  const { request, status, error } = useDeviceGeolocation();

  const isRequesting = status === 'requesting';
  const isUnsupported = status === 'unsupported';

  const handleClick = async () => {
    const next = await request();
    if (next) {
      setFieldValue(latitudeField, next.latitude);
      setFieldValue(longitudeField, next.longitude);
    }
  };

  const tooltip = isUnsupported
    ? t('siteSettings.v2.geolocationUnsupported', 'Geolocation not supported in this browser')
    : status === 'error'
      ? error?.message || t('siteSettings.v2.geolocationError', 'Could not get device location')
      : t('siteSettings.v2.useDeviceLocationTooltip', 'Read latitude and longitude from this device');

  return (
    <Tooltip title={tooltip}>
      <span>
        <Button
          size={size}
          variant="outlined"
          startIcon={isRequesting ? <CircularProgress size={14} /> : <GpsFixedIcon />}
          onClick={handleClick}
          disabled={disabled || isRequesting || isUnsupported}
        >
          {t('siteSettings.v2.useDeviceLocation', 'Use device location')}
        </Button>
      </span>
    </Tooltip>
  );
}
